import { CareerRole } from '../types';

export const CAREER_ROLES: CareerRole[] = [
  {
    id: 'hr-recruiter-compliance',
    title: 'HR Executive (Recruitment & Statutory Compliance)',
    department: 'Human Resources & Talent',
    type: 'Freelance',
    experience: '2+ Years in Publishing / IT Recruitment & Labour Compliance',
    location: '100% Remote (Global)',
    summary: 'Oversee end-to-end talent acquisition for publishing professionals, manage freelancer onboarding SLAs, and maintain HR statutory compliance & labor standards.',
    keyResponsibilities: [
      'Source and screen specialized publishing talent (InDesign compositors, XML engineers, accessibility experts).',
      'Manage freelancer SLA agreements, NDAs, onboarding documentation, and rate contracts.',
      'Maintain statutory compliance, labor regulations, and internal company policies.',
      'Drive employee engagement, quarterly performance reviews, and team retention initiatives.'
    ],
    requiredSkills: [
      'Talent Acquisition & Sourcing',
      'Statutory Compliance & Labour Laws',
      'Freelancer Vendor Onboarding',
      'Contract & NDA Drafting',
      'HR Operations & Payroll Prep'
    ],
    compensation: 'Attractive Retainer + Project-Based Structure',
    badge: 'Core HR',
    urgent: true
  },
  {
    id: 'web-dev-portals',
    title: 'Full-Stack Web Developer (React / Next.js / Publisher Portals)',
    department: 'Web Development & Portals',
    type: 'Freelance',
    experience: '3+ Years in Full-Stack Web Development & API Integrations',
    location: '100% Remote (Global)',
    summary: 'Build custom enterprise web portals, author manuscript submission platforms, interactive e‑learning web applications, publisher admin dashboards, and cloud API integrations using React, Next.js, and Node.js.',
    keyResponsibilities: [
      'Design and build responsive web portals for publisher content management, author submissions, and prepress tracking.',
      'Develop secure REST APIs and WebSocket endpoints for real-time editorial workflow orchestration.',
      'Integrate cloud storage (AWS S3, Firebase, GCP) and automated document conversion services.',
      'Optimize web application performance, accessibility (WCAG 2.1 AA), and responsive mobile experience.'
    ],
    requiredSkills: [
      'React, Next.js, TypeScript & Tailwind CSS',
      'Node.js & Express / Serverless APIs',
      'PostgreSQL / Firestore / Redis Databases',
      'REST & GraphQL API Architecture',
      'Web Security & OAuth 2.0 / Firebase Auth'
    ],
    compensation: 'Attractive Retainer + Project-Based Milestones',
    badge: 'Web & Tech',
    urgent: true
  },
  {
    id: 'sales-business-development',
    title: 'Business Development Manager (Publishing & EdTech BD)',
    department: 'Sales & Business Development',
    type: 'Freelance',
    experience: '3+ Years in B2B Publishing Sales / K-12 & Academic Outreach',
    location: '100% Remote (Global)',
    summary: 'Drive international and domestic client acquisition for prepress, e‑learning, accessibility remediation, and digital conversion services across academic publishers & universities.',
    keyResponsibilities: [
      'Identify and prospect educational publishers, university presses, and e‑learning platforms globally.',
      'Lead technical sales pitch presentations, RFP responses, and commercial proposal estimation.',
      'Build long-term strategic relationships with decision-makers, CTOs, and Production Directors.',
      'Achieve quarterly revenue targets and collaborate with operations for smooth project handoffs.'
    ],
    requiredSkills: [
      'B2B Sales & Account Management',
      'Educational Publishing Industry Knowledge',
      'RFP & Proposal Drafting',
      'Client Prospecting & Lead Gen',
      'Revenue Forecasting'
    ],
    compensation: 'Attractive Retainer + Uncapped Commission Structure',
    badge: 'Growth Role',
    urgent: true
  },
  {
    id: 'operational-head',
    title: 'Head of Publishing Operations (Production & SLAs)',
    department: 'Operations & Management',
    type: 'Freelance',
    experience: '7+ Years Managing High-Volume Prepress & XML Workflows',
    location: '100% Remote (Global)',
    summary: 'Lead and scale end-to-end publishing production, managing prepress typesetting teams, XML digital conversion pipelines, quality control protocols, and client delivery SLAs.',
    keyResponsibilities: [
      'Oversee daily production schedules, page budgets, and project delivery SLAs for international clients.',
      'Optimize workflow automation, AI prepress tools, and quality assurance checkpoints.',
      'Manage resource allocation across in-house leads and external freelance subject matter experts.',
      'Ensure strict adherence to publisher guidelines, turnaround timelines, and defect-free delivery.'
    ],
    requiredSkills: [
      'Prepress & Production Operations Management',
      'Delivery SLA & Quality Control Leadership',
      'Team Leadership & Capacity Planning',
      'InDesign & XML Workflow Optimization',
      'Vendor & Freelance Escalation Management'
    ],
    compensation: 'Competitive Contract Retainer + Performance Bonus',
    badge: 'Operations Lead',
    urgent: true
  },
  {
    id: 'accounts-finance-manager',
    title: 'Accounts & Finance Manager (Billing & Payroll)',
    department: 'Finance & Accounts',
    type: 'Freelance',
    experience: '3+ Years in Corporate Accounting & Billing Operations',
    location: '100% Remote (Global)',
    summary: 'Handle corporate accounting, domestic/international client invoicing, GST/TDS compliance, vendor & freelancer payout management, and financial reporting.',
    keyResponsibilities: [
      'Generate domestic & international client invoices and manage accounts receivable follow-ups.',
      'Process monthly payroll, freelance project payouts, and statutory tax deductions (TDS / GST).',
      'Maintain financial general ledgers, bank reconciliations, and quarterly audit statements.',
      'Prepare monthly P&L statements, cash flow forecasts, and operational budget reports.'
    ],
    requiredSkills: [
      'Tally Prime / QuickBooks',
      'GST & TDS Tax Compliance',
      'Accounts Receivable & Invoicing',
      'Payroll & Freelancer Payouts',
      'Financial Reporting & Budgeting'
    ],
    compensation: 'Project-Wise / Monthly Retainer Structure',
    badge: 'Finance Lead'
  },
  {
    id: 'indesign-typesetter',
    title: 'Senior InDesign Typesetter & Compositor',
    department: 'Composition & Prepress',
    type: 'Freelance',
    experience: '3+ Years in Academic/K-12 Typesetting',
    location: '100% Remote (Global)',
    summary: 'We are seeking experienced desktop publishers proficient in Adobe InDesign for high-volume academic books, journal layout composition, and multi-column math formatting with strict adherence to hyphenation rules, orphan/widow elimination, and baseline grid alignments.',
    keyResponsibilities: [
      'Typeset complex manuscripts using provided Adobe InDesign templates, master grids, and baseline grid alignment.',
      'Enforce strict Hyphenation & Justification (H&J) rules: prevent hyphen stacks (laddering), avoid hyphenation on proper nouns, and restrict hyphens across column/page breaks.',
      'Eliminate orphans, widows, and single-word runovers using precise kerning, tracking (-15 to +15), and story re-flow adjustments.',
      'Ensure flawless baseline grid snapping, optical margin alignment (hanging punctuation), and balanced vertical justification across facing spreads.',
      'Format complex mathematical equations using MathType or InDesign native equation tools with proper symbol kerning.',
      'Incorporate author proof revisions and editorial query corrections accurately with zero introduced errors.',
      'Generate print-ready PDF/X-1a and PDF/X-4 files alongside fully packaged INDD source archives with linked fonts.'
    ],
    requiredSkills: [
      'Adobe InDesign CC Mastery',
      'Hyphenation & Justification (H&J) Rules',
      'Orphan & Widow Control Protocols',
      'Baseline Grid & Optical Margin Alignment',
      'MathType / LaTeX Equation Formatting',
      'Paragraph & Character Styles Hierarchy',
      'Prepress & PDF/X Standards Compliance',
      'Page Budgeting & Spread Balance'
    ],
    compensation: 'Page-Wise / Project-Wise Competitive Rates',
    badge: 'High Demand',
    urgent: true
  },
  {
    id: 'xml-epub-engineer',
    title: 'XML & EPUB Conversion Specialist',
    department: 'Digital Publishing',
    type: 'Freelance',
    experience: '2+ Years in XML/EPUB Transformation',
    location: '100% Remote (Global)',
    summary: 'Join our digital publishing pipeline to convert InDesign/Word manuscripts into validated JATS/BITS XML schemas and reflowable/fixed-layout EPUB 3 e-books.',
    keyResponsibilities: [
      'Transform print files into structured XML following JATS 1.3, BITS, or custom DTDs.',
      'Build responsive EPUB 3.2 packages passing ePubCheck and Kindle guidelines.',
      'Write XSLT stylesheets or custom regex scripts for automated cleanup.',
      'Enrich publication metadata and validate CrossRef indexing structure.'
    ],
    requiredSkills: [
      'JATS / BITS DTD',
      'EPUB 3.2 (Reflow & Fixed)',
      'ePubCheck & Oxygen XML Editor',
      'XSLT, HTML5 & CSS3',
      'Regex & Batch Scripting'
    ],
    compensation: 'Title-Wise / Project-Wise Agreed SLA Rates',
    badge: 'Tech Focused'
  },
  {
    id: 'accessibility-remediator',
    title: 'PDF/UA & WCAG Accessibility Remediator',
    department: 'Accessibility Compliance',
    type: 'Freelance',
    experience: '2+ Years in Document Remediation',
    location: '100% Remote (Global)',
    summary: 'Seeking certified accessibility experts to audit and remediate complex PDF textbooks and Word documents to comply with PDF/UA (ISO 14289-1) and WCAG 2.1 AA.',
    keyResponsibilities: [
      'Tag complex document elements (headings, multi-page tables, lists, figures).',
      'Establish correct reading order and logical structural tree in Acrobat Pro.',
      'Test remediation results using PAC 2021 and NVDA / JAWS screen readers.',
      'Generate VPAT compliance reports and audit documentation.'
    ],
    requiredSkills: [
      'Adobe Acrobat Pro Advanced Tagging',
      'PAC 2021 Audit Tool',
      'WCAG 2.1 AA & PDF/UA Specifications',
      'Screen Readers (NVDA / JAWS / VoiceOver)',
      'Section 508 Standards'
    ],
    compensation: 'Page-Wise / Title-Wise Agreed SLA Rates',
    badge: '100% Remote',
    urgent: true
  },
  {
    id: 'alt-text-writer',
    title: 'STEM & Educational Alt-Text Writer',
    department: 'Editorial & Accessibility',
    type: 'Freelance',
    experience: '1+ Years in Alt-Text or Technical Writing',
    location: '100% Remote (Global)',
    summary: 'Looking for detail-oriented subject matter writers to draft precise, concise alternative text descriptions for STEM diagrams, charts, maps, and mathematical figures.',
    keyResponsibilities: [
      'Analyze scientific, mathematical, and medical illustrations in textbooks.',
      'Write clear, objective long and short alt-text descriptions following Diagram Center guidelines.',
      'Organize alt-text companion logs matched to figure IDs in Word or Excel.',
      'Collaborate with accessibility auditors to ensure screen-reader clarity.'
    ],
    requiredSkills: [
      'STEM Background (Science, Tech, Engineering, Math)',
      'Diagram Center Alt-Text Guidelines',
      'WCAG 1.1.1 Non-Text Content Standards',
      'Strong Written English Communication',
      'Attention to Visual Detail'
    ],
    compensation: 'Per-Figure / Volume-Based Rates'
  },
  {
    id: 'academic-copyeditor',
    title: 'Academic Copyeditor & Proofreader',
    department: 'Editorial Services',
    type: 'Freelance',
    experience: '3+ Years Editorial Experience',
    location: '100% Remote (Global)',
    summary: 'Refine academic manuscripts, research papers, and educational textbooks for language clarity, grammatical accuracy, and strict style guide compliance.',
    keyResponsibilities: [
      'Perform substantive copyediting and proofreading in MS Word using Track Changes.',
      'Ensure strict compliance with style guides (APA 7th, Chicago 17th, MLA, ACS).',
      'Cross-check bibliographies, citations, footnotes, and figure cross-references.',
      'Compile author query logs for ambiguous or missing manuscript content.'
    ],
    requiredSkills: [
      'APA / Chicago / MLA / ACS Style Manuals',
      'MS Word Track Changes & Style Sheets',
      'Academic & Technical English Fluency',
      'Reference & Citation Verification',
      'Proofreading Marks & Standards'
    ],
    compensation: 'Per-Word / Page-Wise Competitive Rates'
  },
  {
    id: 'vector-illustrator',
    title: 'Graphic Designer & Vector Illustrator',
    department: 'Creative & Graphics',
    type: 'Freelance',
    experience: '2+ Years in Educational Graphics',
    location: '100% Remote (Global)',
    summary: 'Redraw and enhance legacy artwork, technical line art, maps, and infographics into scalable, press-ready vector formats for textbook publishing.',
    keyResponsibilities: [
      'Recreate low-res or hand-drawn sketches into clean Adobe Illustrator vectors.',
      'Format vector artwork layers, callout typography, and color separations for press.',
      'Ensure consistent line weights, font usage, and color spaces (CMYK / RGB).',
      'Retouch photographs and high-res images in Photoshop.'
    ],
    requiredSkills: [
      'Adobe Illustrator CC',
      'Adobe Photoshop CC',
      'Technical Line Art & Labeling',
      'Color Management (CMYK / Spot Colors)',
      'EPS, AI, & SVG Production'
    ],
    compensation: 'Per-Artwork / Package-Based Rates'
  },
  {
    id: 'open-freelance-call',
    title: 'General Freelancer Pool (All Roles)',
    department: 'All Departments',
    type: 'Freelance',
    experience: 'Open to All Skill Levels',
    location: '100% Remote (Global)',
    summary: 'Don\'t see your exact role listed? We are always expanding our network of publishing freelancers, QA proofreaders, indexers, and production specialists.',
    keyResponsibilities: [
      'Work on on-demand publishing projects aligned with your specific skill set.',
      'Deliver assignments within agreed SLA timelines with high accuracy.',
      'Communicate proactively with ASAP LEARNING project coordinators.'
    ],
    requiredSkills: [
      'Publishing / Prepress Knowledge',
      'Reliable High-Speed Internet & PC Workstation',
      'Timely Communication & SLA Commitment',
      'Passion for Educational Excellence'
    ],
    compensation: 'Project-Wise / Agreed SLA Rates',
    badge: 'Always Open'
  }
];

export const FREELANCE_BENEFITS = [
  {
    title: '100% Remote Flexibility',
    desc: 'Work from anywhere in the world on your own schedule. Perfect for freelancers seeking steady project streams.',
    iconName: 'Globe'
  },
  {
    title: 'Prompt & Timely Payouts',
    desc: 'Reliable payment processing upon milestone completions with transparent rate agreements.',
    iconName: 'CreditCard'
  },
  {
    title: 'Steady High-Volume Work',
    desc: 'We partner with top global educational publishers, offering continuous project assignments year-round.',
    iconName: 'TrendingUp'
  },
  {
    title: 'AI Tool Access & Upskilling',
    desc: 'Gain exposure to cutting-edge automated publishing scripts, XML validation tools, and AI production pipelines.',
    iconName: 'Sparkles'
  }
];

export interface CategoryAppStat {
  id: string;
  department: string;
  iconName: string;
  applicationCount: number;
  activeOpenings: number;
  popularRole: string;
  growthRate: string;
  badgeColor: string;
  accentBorder: string;
}

export const CATEGORY_APPLICATION_STATS: CategoryAppStat[] = [
  {
    id: 'comp-prepress',
    department: 'Composition & Prepress',
    iconName: 'FileText',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'Senior InDesign Typesetter & Compositor',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-blue-50 text-blue-700 border-blue-200',
    accentBorder: 'hover:border-blue-400'
  },
  {
    id: 'web-dev-portals',
    department: 'Web Development & Portals',
    iconName: 'Globe',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'Full-Stack Web Developer (React / Next.js)',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    accentBorder: 'hover:border-cyan-400'
  },
  {
    id: 'digital-pub',
    department: 'Digital Publishing',
    iconName: 'Code',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'XML & EPUB Conversion Specialist',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    accentBorder: 'hover:border-indigo-400'
  },
  {
    id: 'accessibility',
    department: 'Accessibility Compliance',
    iconName: 'ShieldCheck',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'PDF/UA & WCAG Accessibility Remediator',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    accentBorder: 'hover:border-emerald-400'
  },
  {
    id: 'editorial',
    department: 'Editorial Services',
    iconName: 'BookOpen',
    applicationCount: 0,
    activeOpenings: 2,
    popularRole: 'STEM Alt-Text & Academic Copyeditor',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-purple-50 text-purple-700 border-purple-200',
    accentBorder: 'hover:border-purple-400'
  },
  {
    id: 'hr-talent',
    department: 'Human Resources & Talent',
    iconName: 'Users',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'HR Executive (Recruitment & Compliance)',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-sky-50 text-sky-700 border-sky-200',
    accentBorder: 'hover:border-sky-400'
  },
  {
    id: 'sales-bd',
    department: 'Sales & Business Development',
    iconName: 'TrendingUp',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'Business Development Manager (Publishing BD)',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-amber-50 text-amber-700 border-amber-200',
    accentBorder: 'hover:border-amber-400'
  },
  {
    id: 'ops-mgmt',
    department: 'Operations & Management',
    iconName: 'Briefcase',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'Head of Publishing Operations',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-rose-50 text-rose-700 border-rose-200',
    accentBorder: 'hover:border-rose-400'
  },
  {
    id: 'finance-accts',
    department: 'Finance & Accounts',
    iconName: 'CreditCard',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'Accounts & Finance Manager',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-teal-50 text-teal-700 border-teal-200',
    accentBorder: 'hover:border-teal-400'
  },
  {
    id: 'creative-graphics',
    department: 'Creative & Graphics',
    iconName: 'Palette',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'Graphic Designer & Vector Illustrator',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-orange-50 text-orange-700 border-orange-200',
    accentBorder: 'hover:border-orange-400'
  },
  {
    id: 'general-pool',
    department: 'All Departments',
    iconName: 'Layers',
    applicationCount: 0,
    activeOpenings: 1,
    popularRole: 'General Freelance Talent Pool',
    growthRate: 'Active Hiring',
    badgeColor: 'bg-slate-100 text-slate-700 border-slate-300',
    accentBorder: 'hover:border-slate-400'
  }
];

